import { lazy, Suspense, type ReactNode } from "react";
import {
  Routes,
  Route,
  Outlet,
  useNavigate,
  useParams,
  useLocation,
  matchPath,
  Navigate,
} from "react-router-dom";
import { ErrorPage } from "@safers/ui/templates";
import {
  ProtectedRouter,
  useAuthStore,
  selectUser,
  canAccessSite,
  canAccessMenuSite,
  hasMenu,
  type MenuKey,
} from "@safers/auth";
import { Spinner } from "@safers/ui/atoms";

import { LoginPage } from "@/pages";
import { MobileShell } from "@/components/mobile/MobileShell";
import {
  AREA_ROOT,
  CCTV_PLAYBACK_ROOT,
  CCTV_DEBUG_PATH,
  HOME_PATH,
  LEGACY_REDIRECTS,
  LOGIN_PATH,
  mergeQuery,
  SITE_PARAM,
} from "@/routes/paths";
import { useSignOut } from "@/hooks/useSignOut";
import { homePath } from "@/lib/lastVisit";
import { NoSiteAccess } from "@/components/NoSiteAccess";
import { AppShell } from "@/routes/AppShell";

const ControlLayout = lazy(() =>
  import("@/routes/ControlLayout").then((m) => ({ default: m.ControlLayout }))
);
const HealthLayout = lazy(() =>
  import("@/routes/HealthLayout").then((m) => ({ default: m.HealthLayout }))
);
const SafetyReportPage = lazy(() =>
  import("@/pages/safety-report").then((m) => ({ default: m.SafetyReportPage }))
);
const CctvPage = lazy(() => import("@/pages/cctv").then((m) => ({ default: m.CctvPage })));
const MePage = lazy(() => import("@/pages/me").then((m) => ({ default: m.MePage })));
const CctvDebugPage = lazy(() =>
  import("@/pages/cctv-debug").then((m) => ({ default: m.CctvDebugPage }))
);

function AdminGate({ children }: { children: ReactNode }) {
  const user = useAuthStore(selectUser);
  return user?.authority === "ADMIN" ? <>{children}</> : <Navigate to="/403" replace />;
}

/** `/` — 권한 있는 첫 영역(관제 → 보건 → CCTV)으로 보낸다. 들어갈 영역이 없으면 안내한다. */
function HomeRedirect() {
  const user = useAuthStore(selectUser);
  const to = homePath(user);
  return to ? <Navigate to={to} replace /> : <NoSiteAccess />;
}

function LazyFallback() {
  return (
    <div className="flex h-screen w-screen items-center justify-center">
      <Spinner size="lg" />
    </div>
  );
}

function NotFoundPage() {
  const navigate = useNavigate();
  return (
    <ErrorPage
      variant="404"
      primaryAction={{ label: "홈으로", onClick: () => navigate(HOME_PATH) }}
    />
  );
}

function ForbiddenPage() {
  const navigate = useNavigate();
  const handleLogout = useSignOut();
  return (
    <ErrorPage
      variant="403"
      primaryAction={{ label: "홈으로", onClick: () => navigate(HOME_PATH) }}
      secondaryAction={{ label: "로그아웃", onClick: handleLogout }}
    />
  );
}

function LegacyRedirect() {
  const params = useParams();
  const location = useLocation();
  const siteId = params[SITE_PARAM];
  const rule = LEGACY_REDIRECTS.find((r) => matchPath(r.from, location.pathname));
  if (!rule || !siteId) return <Navigate to={HOME_PATH} replace />;
  return <Navigate to={mergeQuery(rule.to(siteId), location.search, location.hash)} replace />;
}

function SiteGate({ menu, children }: { menu?: MenuKey; children: ReactNode }) {
  const params = useParams();
  const user = useAuthStore(selectUser);
  const siteId = params[SITE_PARAM];
  if (siteId != null && !canAccessSite(user, siteId)) {
    return <Navigate to="/403" replace />;
  }
  if (siteId != null && menu && !canAccessMenuSite(user, siteId, menu)) {
    return <Navigate to="/403" replace />;
  }
  return <>{children}</>;
}

function MenuGate({ menu, children }: { menu: MenuKey; children: ReactNode }) {
  const user = useAuthStore(selectUser);
  if (!hasMenu(user, menu)) {
    return <Navigate to="/403" replace />;
  }
  return <>{children}</>;
}

function AuthLayout() {
  return (
    <ProtectedRouter
      fallback={
        <div className="flex h-screen w-screen items-center justify-center">
          <Spinner size="lg" />
        </div>
      }
    >
      <Outlet />
    </ProtectedRouter>
  );
}

export function AppRoutes() {
  return (
    <Routes>
      <Route path={LOGIN_PATH} element={<LoginPage />} />
      <Route path="/403" element={<ForbiddenPage />} />
      <Route path="/404" element={<NotFoundPage />} />
      <Route element={<AuthLayout />}>
        <Route
          path={CCTV_DEBUG_PATH}
          element={
            <AdminGate>
              <Suspense fallback={<LazyFallback />}>
                <CctvDebugPage />
              </Suspense>
            </AdminGate>
          }
        />
        <Route element={<MobileShell />}>
          <Route element={<AppShell />}>
            <Route
              element={
                <Suspense fallback={<LazyFallback />}>
                  <ControlLayout />
                </Suspense>
              }
            >
              <Route path={AREA_ROOT.control} element={null} />
              <Route path={`${AREA_ROOT.control}/site/:${SITE_PARAM}`} element={null} />
            </Route>
            <Route
              element={
                <Suspense fallback={<LazyFallback />}>
                  <HealthLayout />
                </Suspense>
              }
            >
              <Route path={AREA_ROOT.health} element={null} />
              <Route path={`${AREA_ROOT.health}/site/:${SITE_PARAM}`} element={null} />
            </Route>
            <Route
              path={`${AREA_ROOT.health}/site/:${SITE_PARAM}/records`}
              element={
                <SiteGate menu="HEALTH">
                  <Suspense fallback={<LazyFallback />}>
                    <SafetyReportPage />
                  </Suspense>
                </SiteGate>
              }
            />
            <Route
              element={
                <MenuGate menu="CCTV">
                  <Suspense fallback={<LazyFallback />}>
                    <CctvPage />
                  </Suspense>
                </MenuGate>
              }
            >
              <Route path={AREA_ROOT.cctv} element={null} />
              <Route path={CCTV_PLAYBACK_ROOT} element={null} />
            </Route>
            <Route
              path="me"
              element={
                <Suspense fallback={<LazyFallback />}>
                  <MePage />
                </Suspense>
              }
            />
            <Route index element={<HomeRedirect />} />
            <Route path="dashboard" element={<HomeRedirect />} />
            {LEGACY_REDIRECTS.map(({ from }) => (
              <Route key={from} path={from} element={<LegacyRedirect />} />
            ))}
          </Route>
        </Route>
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
